/**
 * Benchmark — where the airport stands against the airports it is compared
 * with, on every service a board asks about: locally in the Kingdom,
 * regionally in the Gulf, and globally.
 *
 * The airport's own figures come from its KPIs, its finance figures and its
 * green programme. Peer figures are never invented: they are entered from
 * published sources — ACI, ASQ, an airport's own sustainability report —
 * with the year and the source, and the ranking is only ever against the
 * peers that have a value. A blank stays a blank, and the page says how many
 * peers are blank on each metric.
 *
 * Pure. The page gathers, the desk decides.
 */
import type { SourceRef } from "./decision";
import { revenueTotal, type Finance } from "./finance";
import { t } from "../i18n";
import { money } from "./intelligence";
import { findKpi, type Kpi } from "./kpis";

export type PeerSet = "local" | "regional" | "global";

export interface Peer {
  code: string;
  name: string;
  set: PeerSet;
}

export interface Metric {
  key: string;
  label: string;
  unit: string;
  direction: "up" | "down";
  /** what the metric is, for a reader who does not know it */
  what: string;
}

export interface PeerValue {
  value: number;
  year?: number;
  source?: string;
}

export interface BenchmarkEntered {
  /** peer code → metric key → value */
  peers?: Record<string, Record<string, PeerValue>>;
  /** the airport's own figures the platform does not hold: ASQ score, ACA level, turnaround minutes */
  own?: Record<string, PeerValue>;
  /** build 6B: the airport's own peer list, once it has been changed; PEERS until then */
  peerList?: Peer[];
  enteredBy?: string;
  enteredAt?: string;
}

export interface Standing {
  metric: Metric;
  own: number | null;
  ownSource: SourceRef[];
  ownAssumed: boolean;
  bySet: Record<
    PeerSet,
    {
      rank: number | null;
      of: number;
      blank: number;
      leader?: { code: string; name: string; value: number };
      gapToLeader: number | null;
      rows: { code: string; name: string; value: number | null; year?: number; source?: string; self?: boolean }[];
    }
  >;
}

export interface BenchmarkInputs {
  now: Date;
  siteCode: string;
  currency: string;
  kpis: Kpi[];
  finance: Finance | null;
  green: { kgPerPax: number | null; kwhPerPax: number | null; acaLevel: number | null; sources: SourceRef[] } | null;
  entered: BenchmarkEntered;
}

export interface BenchmarkDesk {
  generatedAt: string;
  currency: string;
  peers: Peer[];
  standings: Standing[];
  summary: Record<PeerSet, { lead: number; trail: number; unranked: number }>;
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    text: string;
    tone?: "good" | "bad" | "warn";
    assumed?: boolean;
    sources: SourceRef[];
  }[];
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
}

export const PEERS: Peer[] = [
  { code: "OERK", name: "Riyadh King Khalid", set: "local" },
  { code: "OEJN", name: "Jeddah King Abdulaziz", set: "local" },
  { code: "OEDF", name: "Dammam King Fahd", set: "local" },
  { code: "OEAB", name: "Abha", set: "local" },
  { code: "OETF", name: "Taif", set: "local" },
  { code: "OMDB", name: "Dubai International", set: "regional" },
  { code: "OMAA", name: "Abu Dhabi Zayed", set: "regional" },
  { code: "OTHH", name: "Doha Hamad", set: "regional" },
  { code: "OOMS", name: "Muscat", set: "regional" },
  { code: "OBBI", name: "Bahrain", set: "regional" },
  { code: "OKKK", name: "Kuwait", set: "regional" },
  { code: "EHAM", name: "Amsterdam Schiphol", set: "global" },
  { code: "WSSS", name: "Singapore Changi", set: "global" },
  { code: "VHHH", name: "Hong Kong", set: "global" },
  { code: "LTFM", name: "Istanbul", set: "global" },
  { code: "EGLL", name: "London Heathrow", set: "global" },
  { code: "ENGM", name: "Oslo Gardermoen", set: "global" },
  { code: "KATL", name: "Atlanta", set: "global" },
];

export const METRICS: Metric[] = [
  {
    key: "otp",
    label: "On-time departures",
    unit: "%",
    direction: "up",
    what: "departures within 15 minutes of schedule",
  },
  { key: "asq", label: "ASQ overall satisfaction", unit: "/5", direction: "up", what: "ACI's passenger survey score" },
  {
    key: "queue",
    label: "Worst queue",
    unit: "min",
    direction: "down",
    what: "the longest wait at security or immigration",
  },
  {
    key: "baggage",
    label: "Bags delivered on time",
    unit: "%",
    direction: "up",
    what: "bags on the belt within the standard",
  },
  {
    key: "mttr",
    label: "Mean time to repair, P1",
    unit: "h",
    direction: "down",
    what: "how long a priority-one fault stays open",
  },
  {
    key: "availability",
    label: "Runway availability",
    unit: "%",
    direction: "up",
    what: "hours the runway was open over hours scheduled",
  },
  { key: "revPax", label: "Revenue a passenger", unit: "SAR", direction: "up", what: "total revenue over passengers" },
  {
    key: "nonAero",
    label: "Non-aeronautical share",
    unit: "%",
    direction: "up",
    what: "commercial revenue over total revenue",
  },
  {
    key: "costPax",
    label: "Operating cost a passenger",
    unit: "SAR",
    direction: "down",
    what: "operating cost over passengers",
  },
  { key: "kwhPax", label: "Energy a passenger", unit: "kWh", direction: "down", what: "electricity over passengers" },
  {
    key: "kgPax",
    label: "Carbon a passenger",
    unit: "kgCO₂e",
    direction: "down",
    what: "Scope 1 and 2 over passengers",
  },
  { key: "aca", label: "Airport Carbon Accreditation", unit: "level", direction: "up", what: "ACI's ladder, 1 to 4+" },
  {
    key: "turnaround",
    label: "Minimum turnaround",
    unit: "min",
    direction: "down",
    what: "the narrowbody turnaround the airport supports",
  },
];

/** A metric's label inside a sentence: lower case in English, the translation as written otherwise. */
const inSentence = (label: string): string => {
  const shown = t(label);
  return shown === label ? label.toLowerCase() : shown;
};

export function buildBenchmark(inp: BenchmarkInputs): BenchmarkDesk {
  const cur = inp.currency;
  const peerList = inp.entered.peerList ?? PEERS;
  const kpi = (k: string) => findKpi(inp.kpis, k);
  const f = inp.finance;
  const kpiRef = (k: Kpi): SourceRef => ({ kind: "kpi", ref: k.key, label: k.label, href: "/executive-kpis" });
  const finRef: SourceRef | null = f
    ? f.basis === "demo"
      ? { kind: "demo", ref: "demo finance feed", label: "demonstration figures" }
      : { kind: "finance", ref: "finance", label: "finance figures", enteredBy: f.enteredBy }
    : null;
  const ownEntered = (k: string): { value: number | null; src: SourceRef[] } => {
    const v = inp.entered.own?.[k];
    return v
      ? {
          value: v.value,
          src: [
            {
              kind: "entered",
              ref: v.source ?? "entered",
              label: `${k}${v.year ? ` (${v.year})` : ""}`,
              enteredBy: inp.entered.enteredBy,
            },
          ],
        }
      : { value: null, src: [] };
  };
  const own = (m: Metric): { value: number | null; src: SourceRef[]; assumed: boolean } => {
    switch (m.key) {
      case "otp": {
        const k = kpi("punctuality");
        return { value: k?.value ?? null, src: k ? [kpiRef(k)] : [], assumed: false };
      }
      case "queue": {
        const k = kpi("queue");
        return { value: k?.value ?? null, src: k ? [kpiRef(k)] : [], assumed: false };
      }
      case "baggage": {
        const k = kpi("baggage");
        return { value: k?.value ?? null, src: k ? [kpiRef(k)] : [], assumed: false };
      }
      case "mttr": {
        const k = kpi("mttr_p1_hours");
        return { value: k?.value ?? null, src: k ? [kpiRef(k)] : [], assumed: false };
      }
      case "availability": {
        const k = kpi("runway_availability_pct");
        return { value: k?.value ?? null, src: k ? [kpiRef(k)] : [], assumed: false };
      }
      case "revPax":
        return {
          value: f?.passengers ? Math.round(revenueTotal(f) / f.passengers) : null,
          src: finRef ? [finRef] : [],
          assumed: f?.basis === "demo",
        };
      case "nonAero":
        return {
          value: f
            ? Math.round(
                (1000 * f.revenue.filter((l) => l.key !== "aeronautical").reduce((t, l) => t + l.actual, 0)) /
                  Math.max(1, revenueTotal(f)),
              ) / 10
            : null,
          src: finRef ? [finRef] : [],
          assumed: f?.basis === "demo",
        };
      case "costPax":
        return {
          value: f?.passengers ? Math.round(f.opex.actual / f.passengers) : null,
          src: finRef ? [finRef] : [],
          assumed: f?.basis === "demo",
        };
      case "kwhPax":
        return { value: inp.green?.kwhPerPax ?? null, src: inp.green?.sources ?? [], assumed: true };
      case "kgPax":
        return { value: inp.green?.kgPerPax ?? null, src: inp.green?.sources ?? [], assumed: true };
      case "aca": {
        const e = ownEntered("aca");
        return {
          value: e.value ?? inp.green?.acaLevel ?? null,
          src: e.src.length ? e.src : (inp.green?.sources ?? []),
          assumed: true,
        };
      }
      default: {
        const e = ownEntered(m.key);
        return { value: e.value, src: e.src, assumed: true };
      }
    }
  };
  const standings: Standing[] = METRICS.map((m) => {
    const o = own(m);
    const bySet = {} as Standing["bySet"];
    for (const set of ["local", "regional", "global"] as PeerSet[]) {
      const peers = peerList.filter((p) => p.set === set);
      type Row = { code: string; name: string; value: number | null; year?: number; source?: string; self?: boolean };
      const rows: Row[] = peers.map((p) => {
        const v = inp.entered.peers?.[p.code]?.[m.key];
        return { code: p.code, name: p.name, value: v?.value ?? null, year: v?.year, source: v?.source };
      });
      const valued = rows.filter((r) => r.value != null);
      const all: Row[] =
        o.value != null
          ? [...valued, { code: inp.siteCode, name: "this airport", value: o.value, self: true }]
          : valued;
      all.sort((a, b) => (m.direction === "up" ? (b.value ?? 0) - (a.value ?? 0) : (a.value ?? 0) - (b.value ?? 0)));
      const rank = o.value != null && valued.length ? all.findIndex((r) => r.self) + 1 : null;
      const leader = valued.length ? (all.find((r) => !r.self) ?? undefined) : undefined;
      const best = all[0];
      bySet[set] = {
        rank,
        of: valued.length,
        blank: rows.length - valued.length,
        leader: leader ? { code: leader.code, name: leader.name, value: leader.value! } : undefined,
        gapToLeader:
          o.value != null && best && !best.self && best.value != null
            ? Math.round((o.value - best.value) * 10) / 10
            : null,
        rows: [...all, ...rows.filter((r) => r.value == null)],
      };
    }
    return { metric: m, own: o.value, ownSource: o.src, ownAssumed: o.assumed, bySet };
  });
  const summary = {} as BenchmarkDesk["summary"];
  for (const set of ["local", "regional", "global"] as PeerSet[]) {
    let lead = 0,
      trail = 0,
      unranked = 0;
    for (const st of standings) {
      const b = st.bySet[set];
      if (b.rank == null) unranked++;
      else if (b.rank === 1) lead++;
      else trail++;
    }
    summary[set] = { lead, trail, unranked };
  }
  const entered = Object.values(inp.entered.peers ?? {}).reduce((t, m) => t + Object.keys(m).length, 0);
  const ownKnown = standings.filter((s) => s.own != null).length;
  const tiles: BenchmarkDesk["tiles"] = [
    {
      key: "own",
      label: t("own metrics with a value"),
      value: ownKnown,
      unit: t("of {n}", { n: METRICS.length }),
      text:
        standings
          .filter((s) => s.own == null)
          .map((s) => t(s.metric.label))
          .join("; ") || t("every metric has a value"),
      tone: ownKnown >= 9 ? "good" : "warn",
      sources: [],
    },
    {
      key: "entered",
      label: t("peer values entered"),
      value: entered,
      unit: "",
      text: entered
        ? t("across {n} peers, from published sources", { n: Object.keys(inp.entered.peers ?? {}).length })
        : t("none yet — nothing is ranked until peer values are entered from published sources"),
      tone: entered ? "good" : "warn",
      assumed: true,
      sources: entered
        ? [{ kind: "entered", ref: "entered", label: "peer values", enteredBy: inp.entered.enteredBy }]
        : [],
    },
    ...(["local", "regional", "global"] as PeerSet[]).map((set) => ({
      key: `set:${set}`,
      label: t("{set}: lead / trail / unranked", { set: t(set) }),
      value: null as number | null,
      unit: "",
      text: t("{lead} / {trail} / {unranked} of {n} metrics", {
        lead: summary[set].lead,
        trail: summary[set].trail,
        unranked: summary[set].unranked,
        n: METRICS.length,
      }),
      tone: (summary[set].trail > summary[set].lead ? "warn" : "good") as "warn" | "good",
      sources: [],
    })),
  ];
  const ranked = standings.filter((s) => s.bySet.regional.rank != null);
  const worst = [...ranked].sort((a, b) => (b.bySet.regional.rank ?? 0) - (a.bySet.regional.rank ?? 0))[0];
  const boardLine = [
    t(
      "{site} has a value for {known} of {n} benchmark metrics; {entered} peer values are entered, so {ranked} metrics rank regionally.",
      {
        site: inp.siteCode,
        known: ownKnown,
        n: METRICS.length,
        entered,
        ranked: standings.filter((s) => s.bySet.regional.rank != null).length,
      },
    ),
    worst
      ? worst.bySet.regional.gapToLeader != null
        ? t("The weakest regional standing is {metric}: {rank} of {of}, {gap} {unit} behind {leader}.", {
            metric: inSentence(worst.metric.label),
            rank: worst.bySet.regional.rank!,
            of: worst.bySet.regional.of + 1,
            gap: Math.abs(worst.bySet.regional.gapToLeader),
            unit: worst.metric.unit,
            leader: worst.bySet.regional.leader?.name ?? t("the leader"),
          })
        : t("The weakest regional standing is {metric}: {rank} of {of}, no leader to measure against.", {
            metric: inSentence(worst.metric.label),
            rank: worst.bySet.regional.rank!,
            of: worst.bySet.regional.of + 1,
          })
      : t(
          "No metric ranks yet: enter peer values from the ACI and ASQ publications and each airport's sustainability report.",
        ),
    t(
      "Nothing here is estimated: a peer without a published figure stays blank, and the rank is only ever among those with one.",
    ),
  ];
  const material = materialForBenchmark({ standings, summary, tiles, boardLine, currency: cur } as BenchmarkDesk);
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));
  return {
    generatedAt: inp.now.toISOString(),
    currency: cur,
    peers: peerList,
    standings,
    summary,
    tiles,
    boardLine,
    gaps: [
      ...(entered ? [] : [t("no peer values entered")]),
      ...standings
        .filter((s) => s.own == null)
        .map((s) => t("no own value for {metric}", { metric: inSentence(s.metric.label) })),
    ],
    figures,
  };
}

export const BENCHMARK_MODEL_PROMPT = [
  "You are advising an airport's chief executive on how it compares with other airports, from the material given and nothing else.",
  "You may explain, rank and argue; you must not state any number that is not in the material, and you must not supply a figure for any airport.",
  "Say when a figure is a demonstration or entered value. Be brief and direct.",
].join(" ");

export function materialForBenchmark(
  d: Pick<BenchmarkDesk, "standings" | "summary" | "tiles" | "boardLine" | "currency">,
): string {
  const L: string[] = [];
  for (const t of d.tiles) L.push(`Tile ${t.label}: ${t.value ?? "n/a"} ${t.unit} — ${t.text}`);
  for (const s of d.standings) {
    L.push(
      `Metric ${s.metric.key} "${s.metric.label}" (${s.metric.unit}, ${s.metric.direction === "up" ? "higher is better" : "lower is better"}): own ${s.own ?? "n/a"}${s.ownAssumed ? " [demo/entered]" : ""}.`,
    );
    for (const set of ["local", "regional", "global"] as PeerSet[]) {
      const b = s.bySet[set];
      L.push(
        `  ${set}: rank ${b.rank ?? "unranked"} of ${b.of + (s.own != null ? 1 : 0)}, ${b.blank} blank${b.leader ? `, leader ${b.leader.name} ${b.leader.value}` : ""}${b.gapToLeader != null ? `, gap ${b.gapToLeader}` : ""}. ${b.rows
          .filter((r) => r.value != null)
          .map((r) => `${r.name} ${r.value}${r.year ? ` (${r.year})` : ""}`)
          .join("; ")}`,
      );
    }
  }
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerBenchmark(q: string, d: BenchmarkDesk): string | null {
  const t = q.toLowerCase();
  const set: PeerSet = /global|world/.test(t) ? "global" : /local|kingdom|saudi|ksa/.test(t) ? "local" : "regional";
  if (/weak|worst|behind|trail/.test(t)) {
    const rows = d.standings
      .filter((s) => s.bySet[set].rank != null && s.bySet[set].rank! > 1)
      .sort((a, b) => (b.bySet[set].rank ?? 0) - (a.bySet[set].rank ?? 0));
    return (
      rows
        .map(
          (s) =>
            `${s.metric.label}: ${s.bySet[set].rank} of ${s.bySet[set].of + 1}${s.bySet[set].gapToLeader != null ? `, ${Math.abs(s.bySet[set].gapToLeader!)} ${s.metric.unit} behind ${s.bySet[set].leader?.name}` : ""}`,
        )
        .join("\n") || `Nothing trails ${set}ly among the metrics with peer values.`
    );
  }
  if (/lead|best|ahead/.test(t))
    return (
      d.standings
        .filter((s) => s.bySet[set].rank === 1)
        .map((s) => `${s.metric.label}: first of ${s.bySet[set].of + 1}`)
        .join("\n") || `No metric leads ${set}ly yet.`
    );
  if (/blank|missing|unranked|enter/.test(t))
    return (
      d.standings
        .filter((s) => s.bySet[set].rank == null)
        .map((s) => `${s.metric.label}: ${s.own == null ? "no own value" : `${s.bySet[set].blank} peers blank`}`)
        .join("\n") || "Every metric ranks."
    );
  if (/board|summar/.test(t)) return d.boardLine.join(" ");
  const m = d.standings.find((s) => t.includes(s.metric.key) || t.includes(s.metric.label.toLowerCase().slice(0, 10)));
  if (m) {
    const b = m.bySet[set];
    return `${m.metric.label}: own ${m.own ?? "no value"} ${m.metric.unit}; ${set} rank ${b.rank ?? "unranked"} of ${b.of + (m.own != null ? 1 : 0)}, ${b.blank} peers blank${b.leader ? `; leader ${b.leader.name} at ${b.leader.value}` : ""}.`;
  }
  if (/revenue|cost/.test(t))
    return d.standings
      .filter((s) => ["revPax", "costPax"].includes(s.metric.key))
      .map((s) => `${s.metric.label}: ${s.own != null ? money(s.own, d.currency) : "no value"}`)
      .join("\n");
  return null;
}
